import { IconType } from 'components/Icon';
import forge from 'apis/forge';
import { SceneDsLink } from 'dragonmouse-shared/interfaces';
import { toast } from 'react-toastify';
import { DsSection, DsTabKind, TokenCategory } from './types';

// Tab chrome (icon + label) per DsTab kind.
export const DS_TAB_CHROME: Record<
	DsTabKind,
	{
		icon: IconType;
		label: string;
	}
> = {
	foundations: { icon: IconType.STACK_LINE, label: 'Foundations' },
	guidelines: { icon: IconType.BOOK_OPEN, label: 'Guidelines' },
	colors: { icon: IconType.PALETTE, label: 'Colors' },
	typography: { icon: IconType.TEXT, label: 'Typography' },
	styles: { icon: IconType.CONTRAST_DROP, label: 'Styles' },
	components: { icon: IconType.PUZZLE2, label: 'Components' },
	gallery: { icon: IconType.LAYOUT, label: 'Gallery' },
	assets: { icon: IconType.ATTACHMENT_2, label: 'Assets' },
};

// Chrome for chat-card / show-action display of a DsSection ("Theme Updated",
// etc.). Decoupled from DS_TAB_CHROME because sections and tab kinds are
// different concepts even though most values overlap.
export const DS_SECTION_CHROME: Record<
	DsSection,
	{
		icon: IconType;
		label: string;
	}
> = {
	foundations: { icon: IconType.STACK_LINE, label: 'Foundations' },
	theme: { icon: IconType.PALETTE, label: 'Theme' },
	styles: { icon: IconType.CONTRAST_DROP, label: 'Styles' },
	components: { icon: IconType.PUZZLE2, label: 'Components' },
	gallery: { icon: IconType.LAYOUT, label: 'Gallery' },
	guidelines: { icon: IconType.BOOK_OPEN, label: 'Guidelines' },
	assets: { icon: IconType.ATTACHMENT_2, label: 'Assets' },
};

// Tab kinds that don't enumerate items underneath them — they're single-page
// views without per-item drill-downs. Their tab id is always `root-${kind}`,
// never `${kind}-${contentPath}`.
export const CHILDLESS_TAB_KINDS: ReadonlySet<DsTabKind> = new Set([
	'foundations',
	'colors',
	'typography',
	'styles',
]);

// Single source of truth for tabId composition. Childless kinds and assets
// always use `root-${kind}` (contentPath is meaningless for them or, for assets,
// stored on the DsTab while the tab id stays fixed). Everything else uses
// `${kind}-${contentPath}` when a contentPath is provided.
export const buildTabId = (kind: DsTabKind, contentPath?: string): string => {
	if (kind === 'assets' || CHILDLESS_TAB_KINDS.has(kind)) return `root-${kind}`;
	return contentPath ? `${kind}-${contentPath}` : `root-${kind}`;
};

// Maps a token's category to the DsTab kind that displays it.
// color → Colors, typography → Typography, everything else → Styles.
export const tokenCategoryToTabKind = (category: TokenCategory): DsTabKind => {
	if (category === 'color') return 'colors';
	if (category === 'typography') return 'typography';
	return 'styles';
};

// The conversion boundary between section-side (chat agent's vocabulary) and
// tab-side (UI). Given a section and the per-link `contentPath` produced by
// `pathsToDsLinks`, returns the tab kind to open and any contentPath to pass.
//
// - `theme` section: contentPath is a token category key ('color', 'spacing',
//   etc.) — fold into Colors/Typography/Styles. No contentPath downstream.
// - `styles` section: always Styles tab (recipe-level focus is not surfaced
//   at the chat-card level).
// - `foundations` section + `config/fonts*`: Typography tab — fonts moved there
//   when the DS editor was restructured; other config files stay on Foundations.
// - Everything else: 1:1 with its tab kind, contentPath passes through.
export const sectionToTabTarget = (
	section: DsSection,
	contentPath: string | undefined
): { kind: DsTabKind; contentPath?: string } => {
	if (section === 'theme') {
		const category = contentPath as TokenCategory | undefined;
		return { kind: category ? tokenCategoryToTabKind(category) : 'colors' };
	}
	if (section === 'styles') {
		return { kind: 'styles' };
	}
	if (section === 'foundations' && contentPath?.startsWith('config/fonts')) {
		return { kind: 'typography' };
	}
	return { kind: section, contentPath };
};

// Fetches a SceneDsLink for a design system: the DS itself, the best usable published
// version, and the latest published version number. Toasts and returns null on failure.
export const fetchSceneDsLink = async (designSystemId: string): Promise<SceneDsLink | null> => {
	try {
		return await forge.getSceneDsLink(designSystemId);
	} catch (error: any) {
		const message = error?.response?.data?.error;
		if (message?.includes('no published version')) {
			toast.warning(
				'This design system has no published version yet. If you just created it, please wait a couple of minutes and try again.'
			);
		} else if (message?.includes('failed to publish')) {
			toast.warning('This design system failed to publish. Please try publishing again.');
		} else if (message?.includes('not ready')) {
			toast.warning(
				'This design system is not ready for use yet. Please wait a couple of minutes and try again.'
			);
		} else {
			console.error('Failed to fetch design system link:', error);
			toast.error('Failed to load design system. Please try again.');
		}
		return null;
	}
};
